import type { FontMode, Theme } from '@momomail/shared';
import { DEFAULT_SETTINGS } from '@momomail/shared';

const media = window.matchMedia('(prefers-color-scheme: dark)');
let current: Theme = 'system';

function paint() {
  const dark = current === 'dark' || (current === 'system' && media.matches);
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
}

media.addEventListener('change', paint);

/** Theme: follow system / light / dark. */
export function applyTheme(theme: Theme) {
  current = theme;
  paint();
}

/** The name of the Momo font in CSS (--marker in theme.css). */
const MARKER = 'Momo Marker';

interface Face {
  url: string;
  unicodeRange: string;
}
let faces: Promise<Face[]> | null = null;

/**
 * Momo font: LXGW Marker Gothic (SIL OFL 1.1), split into small chunks in public/fonts/marker (fonts/manifest.json lists each chunk's character range).
 * Registered as "Momo Marker"; via unicode-range the browser downloads only the chunks containing characters used on the page; falls back to the system font when unavailable.
 */
export function registerUiFont(): void {
  faces = fetch('./fonts/manifest.json')
    .then((r) => (r.ok ? (r.json() as Promise<{ fonts: { id: string; faces: Face[] }[] }>) : null))
    .then((m) => {
      const list = (m?.fonts.find((f) => f.id === 'marker')?.faces ?? []).map((f) => ({ ...f, url: `.${f.url}` }));
      for (const face of list) document.fonts.add(new FontFace(MARKER, `url(${face.url}) format("woff2")`, { unicodeRange: face.unicodeRange, display: 'swap' }));
      return list;
    })
    .catch(() => []);
}

const FONT_KEY = 'momomail.font';

/** Font setting (all / ui / system, see FontMode): theme.css swaps --sans / --display / --mail based on html[data-font]. Keep a copy on this device so the next launch applies it right away (without waiting for settings to load). */
export function applyFont(mode: FontMode | undefined) {
  // Old servers don't know this setting: use the default
  if (mode !== 'all' && mode !== 'ui' && mode !== 'system') mode = DEFAULT_SETTINGS.font;
  document.documentElement.dataset.font = mode;
  try {
    localStorage.setItem(FONT_KEY, mode);
  } catch {
    // If it can't be stored, wait for settings to load every time
  }
}

export function savedFont(): FontMode {
  try {
    const v = localStorage.getItem(FONT_KEY);
    if (v === 'all' || v === 'ui' || v === 'system') return v;
  } catch {
    // use the default
  }
  return DEFAULT_SETTINGS.font;
}

/** Which chunks contain the characters used in the body: ranges like "U+4E00-4E5F,U+3002". */
function covers(range: string, codes: Set<number>): boolean {
  for (const part of range.split(',')) {
    const [a, b] = part.trim().replace(/^U+/i, '').split('-');
    const lo = parseInt(a!, 16);
    const hi = b ? parseInt(b, 16) : lo;
    for (const c of codes) if (c >= lo && c <= hi) return true;
  }
  return false;
}

const buffers = new Map<string, Promise<ArrayBuffer | null>>();

/**
 * With "全部用馍馍字体" (Momo font everywhere), the mail body (sandbox iframe) uses it too. The body document's CSP only allows data: fonts, not loading by URL,
 * so fetch the chunks used by the body from outside (the browser caches them) and hand them as binary to a FontFace inside the iframe;
 * places where the mail specifies its own font keep using the mail's font.
 */
export async function markerInFrame(doc: Document): Promise<void> {
  const win = doc.defaultView as (Window & typeof globalThis) | null;
  if (!win || !doc.body || !faces) return;
  const codes = new Set<number>();
  for (const ch of doc.body.textContent ?? '') if (ch.codePointAt(0)! > 0x20) codes.add(ch.codePointAt(0)!);
  const needed = (await faces).filter((f) => covers(f.unicodeRange, codes));
  await Promise.all(
    needed.map(async (f) => {
      if (!buffers.has(f.url)) buffers.set(f.url, fetch(f.url).then((r) => (r.ok ? r.arrayBuffer() : null)).catch(() => null));
      const data = await buffers.get(f.url)!;
      if (data) doc.fonts.add(new win.FontFace(MARKER, data, { unicodeRange: f.unicodeRange }));
    }),
  );
  const style = doc.createElement('style');
  style.textContent = `body{font-family:'${MARKER}','PingFang SC','Microsoft YaHei','Segoe UI',system-ui,sans-serif}`;
  doc.head.append(style);
}
